import * as React from 'react';
import styled, { css } from 'styled-components';
import { colors } from '../colors/color';

type RadioButtonOption = { text:string, value:string, selectedColorProfile?:RadioButtonButtonColorProfile, leadingContent?:JSX.Element };
type RadioButtonButtonListProps = {
    groupName:string,
    disabled?:boolean,
    options:RadioButtonOption[],
    selectedValue:string,
    onChange:(selectedValue:string)=>void,
}

export function RadioButtonButtonList(props:RadioButtonButtonListProps)
{
    return (
        <Wrapper columnCount={props.options.length}>
            {props.options.map((o,i) => {
                return (
                        <RadioButtonButton
                            key={i}
                            name={props.groupName}
                            id={props.groupName + "radio_" + i}
                            text={o.text}
                            value={o.value}
                            selectedColorProfile={o.selectedColorProfile}
                            checked={o.value === props.selectedValue}
                            disabled={props.disabled}
                            leadingContent={o.leadingContent}
                            roundedCorner={i===0 ? 'left' : i===props.options.length-1 ? 'right' : undefined}
                            border={i===0 ? 'both' : 'right'}
                            onChange={props.onChange}
                        />
                );
            })}
        </Wrapper>
    );
}

const Wrapper = styled.div<{columnCount:number}>`
    display:grid;
    grid-template-columns: ${props => `repeat(${props.columnCount}, minmax(0, 1fr))`};
`

export type RadioButtonButtonColorProfile = {
    backgroundSelected:string,
    text:string,
    textHover:string
}

type RadioButtonButtonProps = {
    id:string,
    name:string, // radio group
    value:string,
    leadingContent?:JSX.Element,
    text:string,
    checked:boolean,
    disabled?:boolean,
    selectedColorProfile?:RadioButtonButtonColorProfile,
    roundedCorner?: 'left' | 'right',
    border:'left'|'right'|'both'|'none',
    onChange:(value:string)=>void
}
export function RadioButtonButton(props:RadioButtonButtonProps):JSX.Element
{
    const selectedProfile:RadioButtonButtonColorProfile = 
        props.selectedColorProfile || colors.radioButtonButton;

    return (
        <div>
            <RadioInput type="radio" 
                id={props.id} 
                name={props.name} 
                value={props.value} 
                checked={props.checked} 
                disabled={props.disabled} 
                profile={selectedProfile}
                onChange={() => props.onChange(props.value)} />
            
            <RadioLabel htmlFor={props.id} 
                border={props.border} 
                roundedCorner={props.roundedCorner ?? 'left'}
                profile={selectedProfile}
                disabled={props.disabled ?? false}>

                    {props.leadingContent && props.leadingContent}
                    {props.text}

            </RadioLabel>
        </div>
    );
}

const RadioInput = styled.input<{profile:RadioButtonButtonColorProfile}>`
    opacity: 0;
    position: fixed;
    width: 0;
    &:checked + label {
        background-color:${props => props.profile.backgroundSelected};
        color:${props => props.profile.text};
    }
    &:focus + label {
        background-color:${props => props.profile.backgroundSelected};
        color:${props => props.profile.textHover};
    }
`;

const RadioLabel = styled.label<{roundedCorner:'left' | 'right', border:'left'|'right'|'both'|'none', disabled:boolean, profile:RadioButtonButtonColorProfile}>`
    display: flex;
    gap:4px;
    height:100%;
    justify-content: space-around;
    align-items: center;
    padding: 10px 20px;
    font-family: sans-serif, Arial;
    font-size: 14px;
    white-space: nowrap;
    border-style: solid;
    border-color: ${colors.radioButtonButton.border};
    
    background-color: ${props => props.disabled ? colors.radioButtonButton.backgroundDisabled : colors.radioButtonButton.background};
    color: ${props => props.disabled ? colors.radioButtonButton.textDisabled : colors.radioButtonButton.text};

    border-left-width: ${props => props.border === 'left' || props.border === 'both' ? '1px' : '0'};
    border-right-width: ${props => props.border === 'right' || props.border === 'both' ? '1px' : '0'};
    border-top-width: 1px;      
    border-bottom-width: 1px;
    
    border-top-left-radius: ${props => props.roundedCorner === 'left' ? '4px' : '0'};
    border-bottom-left-radius: ${props => props.roundedCorner === 'left' ? '4px' : '0'};
    border-top-right-radius: ${props => props.roundedCorner === 'right' ? '4px' : '0'};
    border-bottom-right-radius: ${props => props.roundedCorner === 'right' ? '4px' : '0'};

    ${props => !props.disabled  && css`
        :hover {
            cursor: pointer;
            background-color: ${props.profile.backgroundSelected};
            color: ${props.profile.textHover};
    `}
`;